import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { catchError, combineLatest, distinctUntilChanged, forkJoin, of, Subscription } from 'rxjs';

import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import {
  CreateSpecialistDowntimeRequest,
  SpecialistDowntimeImpactPreview,
  SpecialistDowntimeItem,
  SpecialistDowntimeListSort,
} from '../data-access/specialist-downtime.models';
import { SpecialistDowntimeService } from '../data-access/specialist-downtime.service';
import {
  SpecialistDowntimeClinicOption,
  SpecialistDowntimeFormComponent,
} from '../components/specialist-downtime-form.component';
import { SpecialistDowntimeQuickViewModalComponent } from '../components/specialist-downtime-quick-view-modal.component';
import { SpecialistDowntimeTableComponent } from '../components/specialist-downtime-table.component';
import { DowntimeImpactSummaryComponent } from '../../downtime/components/downtime-impact-summary.component';
import { AuthService } from '../../../core/auth/auth.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { SpecialistQuickViewModalComponent } from '../../specialists/components/specialist-quick-view-modal.component';
import {
  ErrorBlockComponent,
  FloatingActionFabComponent,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';

@Component({
  selector: 'app-specialist-downtime-list-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CompanyContextRequiredComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    PageContextGuardComponent,
    PageActionModalComponent,
    PagedFeedComponent,
    SpecialistDowntimeFormComponent,
    DowntimeImpactSummaryComponent,
    SpecialistDowntimeQuickViewModalComponent,
    SpecialistDowntimeTableComponent,
    SpecialistQuickViewModalComponent,
    TranslatePipe,
    PageShellComponent,
  ],
  template: `
    <lib-page-shell>
    <lib-page-context-guard [blocked]="companyContextMissing">
      <app-company-context-required />
    </lib-page-context-guard>

    @if (!companyContextMissing) {
      @if (actionErrorText) {
        <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
      }

      <lib-paged-feed
        [items]="items"
        [collectionTemplate]="downtimeTableTemplate"
        [loading]="isLoading"
        [loadingMore]="isLoadingMore"
        [hasMore]="hasMoreItems"
        [errorText]="errorText"
        emptyText="No downtime records found"
        loadMoreText="Load more downtime"
        loadingMoreText="Loading more downtime..."
        retryText="Retry"
        (loadMore)="loadMoreDowntime()"
        (retry)="refresh()"
      />

      <ng-template #downtimeTableTemplate let-items="items">
        <app-specialist-downtime-table
          [items]="items"
          [clinics]="clinicOptions"
          [specialists]="specialistOptions"
          [busyItemId]="busyItemId"
          [canModerateRequests]="canModerateRequests"
          [canManageDowntime]="canManageDowntime"
          [canFinishDowntime]="canFinishDowntime"
          [canOpenSpecialist]="canOpenSpecialist"
          [showSpecialistColumn]="showSpecialistColumn"
          (deleteClicked)="onDelete($event)"
          (resolveClicked)="onResolve($event)"
          (approveClicked)="onApprove($event)"
          (rejectClicked)="onReject($event)"
          (specialistOpenClicked)="openSpecialistQuickView($event)"
          (rowClicked)="openDowntimeQuickView($event)"
        />
      </ng-template>
      }

      @if (isSpecialistQuickViewOpen && selectedSpecialistUserId) {
        <app-specialist-quick-view-modal
          [isOpen]="true"
          [specialistUserId]="selectedSpecialistUserId"
          title="Specialist Quick View"
          (close)="closeSpecialistQuickView()"
        />
      }

      @if (isDowntimeQuickViewOpen && selectedDowntime) {
        <app-specialist-downtime-quick-view-modal
          [isOpen]="true"
          [item]="selectedDowntime"
          [clinics]="clinicOptions"
          [specialists]="specialistOptions"
          (close)="closeDowntimeQuickView()"
        />
      }

      @if (isCreateModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Create Specialist Downtime"
          subtitle="Manual specialist unavailability registration"
          width="760px"
          [canClose]="!isCreateSubmitting"
          (close)="closeCreateModal()"
        >

            @if (createPreviewImpact && pendingCreatePayload) {
              <app-downtime-impact-summary
                [affectedAppointmentIds]="createPreviewImpact.affectedAppointmentIds"
                [reachabilityImpact]="createPreviewImpact.reachabilityImpact"
              />

              <div class="downtime-page__preview-actions">
                <button
                  type="button"
                  class="downtime-page__modal-secondary"
                  [disabled]="isCreateSubmitting"
                  (click)="backToCreateForm()"
                >
                  {{ 'Back to edit' | t }}
                </button>
                <button
                  type="button"
                  class="downtime-page__modal-primary"
                  [disabled]="isCreateSubmitting"
                  (click)="confirmCreateDowntime()"
                >
                  {{ (isCreateSubmitting ? 'Creating...' : 'Create downtime') | t }}
                </button>
              </div>
            }

            @if (createErrorText) {
              <lib-error-block variant="error" [message]="createErrorText" />
            }

            @if (!createPreviewImpact) {
              <app-specialist-downtime-form
                [isSubmitting]="isCreateSubmitting"
                [errorText]="createErrorText"
                [clinics]="clinicOptions"
                [specialists]="specialistOptions"
                [initialValue]="pendingCreatePayload"
                (submitted)="onCreateSubmitted($event)"
              />
            }
        </lib-page-action-modal>
      }

      @if (!companyContextMissing) {
        <lib-floating-action-fab
          [menuTemplate]="downtimeQuickActionsMenu"
          buttonAriaLabel="Open specialist downtime quick actions"
        />

        <ng-template #downtimeQuickActionsMenu let-close="close">
          <div class="downtime-page__fab-menu">
            @if (canCreateDowntimeRequest) {
              <button
                type="button"
                role="menuitem"
                class="action-menu__item"
                [disabled]="!canOpenCreateModal"
                (click)="openCreateModal(); close()"
              >
                {{ 'Create downtime' | t }}
              </button>
            }
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="openFilterModal(); close()"
            >
              Filters & Sort
            </button>
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="refresh(); close()"
            >
              Refresh
            </button>
          </div>
        </ng-template>
      }

      @if (isFilterModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Filters & Sort"
          subtitle="Specialist, status and sort controls for downtime list"
          width="760px"
          (close)="closeFilterModal()"
        >

            <form
              [formGroup]="filtersForm"
              (submit)="$event.preventDefault(); onApplyFilters(); closeFilterModal()"
              class="downtime-page__modal-filters"
            >
              <div class="downtime-page__filters-grid">
                @if (!isSystemAdmin) {
                  <div class="downtime-page__filters-field">
                    <label for="specialist-downtime-filters-clinicId">{{ 'Clinic' | t }}</label>
                    <select
                      id="specialist-downtime-filters-clinicId"
                      formControlName="clinicId"
                      (change)="onClinicFilterChanged()"
                    >
                      <option value="">{{ 'All' | t }}</option>
                      @for (item of clinicOptions; track item.id) {
                        <option [value]="item.id">{{ item.label }}</option>
                      }
                    </select>
                  </div>
                }

                <div class="downtime-page__filters-field">
                  <label for="specialist-downtime-filters-specialistUserId">{{ 'Specialist' | t }}</label>
                  <select id="specialist-downtime-filters-specialistUserId" formControlName="specialistUserId">
                    <option value="">{{ 'slots.allSpecialists' | t }}</option>
                    @for (item of filteredSpecialistOptions; track item.id) {
                      <option [value]="item.id">{{ item.label }}</option>
                    }
                  </select>
                </div>

                <div class="downtime-page__filters-field">
                  <label for="specialist-downtime-filters-status">{{ 'common.status' | t }}</label>
                  <select id="specialist-downtime-filters-status" formControlName="status">
                    <option value="all">{{ 'All' | t }}</option>
                    @for (status of statuses; track status) {
                      <option [value]="status">{{ downtimeStatusLabel(status) }}</option>
                    }
                  </select>
                </div>

                <label class="downtime-page__checkbox-field">
                  <input type="checkbox" formControlName="pendingFirst" />
                  <span>Pending first</span>
                </label>

                <div class="downtime-page__filters-field">
                  <label for="specialist-downtime-filters-sort">Sort</label>
                  <select id="specialist-downtime-filters-sort" formControlName="sort">
                    <option value="startTimeDesc">Newest start</option>
                    <option value="startTimeAsc">Oldest start</option>
                    <option value="expectedRepairUntilDesc">Latest repair until</option>
                    <option value="expectedRepairUntilAsc">Earliest repair until</option>
                    <option value="createdAtDesc">Newest created</option>
                    <option value="createdAtAsc">Oldest created</option>
                    <option value="updatedAtDesc">Newest updated</option>
                    <option value="updatedAtAsc">Oldest updated</option>
                    <option value="statusAsc">Status A-Z</option>
                    <option value="statusDesc">Status Z-A</option>
                  </select>
                </div>
              </div>

              <div class="downtime-page__modal-actions">
                <button type="button" class="downtime-page__modal-secondary" (click)="onResetFilters(); closeFilterModal()">
                  {{ 'Reset' | t }}
                </button>
                <button type="submit" class="downtime-page__modal-primary">
                  {{ 'Apply Filters' | t }}
                </button>
              </div>
            </form>
        </lib-page-action-modal>
      }
    </lib-page-shell>
  `,
  styles: [`
    .downtime-page__create-button {
      height: 40px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 16px;
      cursor: pointer;
      white-space: nowrap;
    }

    .downtime-page__moderation-hint {
      margin-bottom: 12px;
      padding: 10px 12px;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #eff6ff;
      color: #1d4ed8;
      font-size: 14px;
      font-weight: 500;
    }

    .downtime-page__filters-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }

    .downtime-page__filters-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .downtime-page__filters-field label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .downtime-page__filters-field select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      box-sizing: border-box;
    }

    .downtime-page__checkbox-field {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 40px;
      padding-top: 21px;
      color: #374151;
      font-size: 14px;
      font-weight: 600;
      box-sizing: border-box;
    }

    .downtime-page__checkbox-field input {
      width: 16px;
      height: 16px;
      margin: 0;
    }

    .downtime-page__fab-menu-grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
      width: 56px;
    }

    .downtime-page__fab-menu-grid button.downtime-page__fab-action {
      width: 48px;
      min-width: 48px;
      min-height: 48px;
      justify-content: center;
      padding: 0;
      border-radius: 14px;
    }

    .downtime-page__fab-icon {
      position: relative;
      display: inline-flex;
      width: 18px;
      height: 18px;
      color: #0f172a;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .downtime-page__fab-icon--filter::before {
      content: '';
      width: 14px;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 8px solid currentColor;
      position: absolute;
      top: 1px;
    }

    .downtime-page__fab-icon--filter::after {
      content: '';
      position: absolute;
      top: 10px;
      width: 4px;
      height: 6px;
      background: currentColor;
      border-radius: 0 0 2px 2px;
    }

    .downtime-page__modal-filters {
      display: grid;
      gap: 16px;
    }

    .downtime-page__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .downtime-page__preview-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin: 16px 0;
    }

    .downtime-page__modal-secondary,
    .downtime-page__modal-primary {
      min-height: 40px;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .downtime-page__modal-secondary {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
    }

    .downtime-page__modal-primary {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    .downtime-page__modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .downtime-page__modal {
      width: min(760px, 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.97));
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
      padding: 22px;
      box-sizing: border-box;
    }

    .downtime-page__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .downtime-page__modal-title {
      margin: 0;
      font-size: 24px;
      line-height: 1.1;
      color: #111827;
    }

    .downtime-page__modal-subtitle {
      margin: 8px 0 0;
      color: #6b7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .downtime-page__modal-close {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      color: #475569;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    @media (max-width: 900px) {
      .downtime-page__filters-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .downtime-page__modal {
        width: min(100%, 760px);
        max-height: min(92vh, 960px);
        padding: 18px;
        border-radius: 20px;
      }
    }

    @media (max-width: 640px) {
      .downtime-page__filters-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 14px;
      }

      .downtime-page__modal-backdrop {
        padding: 0;
        align-items: flex-end;
      }

      .downtime-page__modal {
        width: 100%;
        max-height: 92vh;
        border-radius: 24px 24px 0 0;
        padding: 16px;
      }

      .downtime-page__modal-head {
        gap: 12px;
      }

      .downtime-page__modal-title {
        font-size: 22px;
      }

      .downtime-page__create-button,
      .downtime-page__modal-secondary,
      .downtime-page__modal-primary {
        width: 100%;
      }

      .downtime-page__modal-actions {
        flex-direction: column;
      }

      .downtime-page__preview-actions {
        flex-direction: column;
      }
    }
  `]
})
export class SpecialistDowntimeListPageComponent implements OnInit, OnDestroy
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly downtimeService = inject(SpecialistDowntimeService);
  private readonly settingsService = inject(SettingsService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();

  protected items: SpecialistDowntimeItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected busyItemId: string | null = null;
  protected canModerateRequests = false;
  protected canManageDowntime = false;
  protected canFinishDowntime = false;
  protected canCreateDowntimeRequest = false;
  protected canOpenSpecialist = false;
  protected showSpecialistColumn = true;
  protected clinicOptions: SpecialistDowntimeClinicOption[] = [];
  protected specialistOptions: Array<{ id: string; label: string; clinicId: string | null }> = [];
  protected isCreateModalOpen = false;
  protected isSpecialistQuickViewOpen = false;
  protected isDowntimeQuickViewOpen = false;
  protected isCreateSubmitting = false;
  protected isReferenceOptionsLoading = false;
  protected isFilterModalOpen = false;
  protected createErrorText: string | null = null;
  protected createPreviewImpact: SpecialistDowntimeImpactPreview | null = null;
  protected pendingCreatePayload: CreateSpecialistDowntimeRequest | null = null;
  protected isSystemAdmin = false;
  protected selectedGlobalClinicId: string | null = null;
  protected selectedSpecialistUserId: string | null = null;
  protected selectedDowntime: SpecialistDowntimeItem | null = null;
  private referenceOptionsLoadedForCompanyId: string | null = null;
  private nextCursor: string | null = null;
  private readonly pageSize = 20;
  protected readonly statuses: Array<'pending_approval' | 'active' | 'resolved' | 'rejected'> = [
    'pending_approval',
    'active',
    'resolved',
    'rejected',
  ];
  protected readonly filtersForm = this.formBuilder.group({
    clinicId: [''],
    specialistUserId: [''],
    status: ['all'],
    pendingFirst: [true],
    sort: ['startTimeDesc'],
  });
  private appliedFilters: {
    clinicId?: string;
    specialistUserId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst: boolean;
    sort: SpecialistDowntimeListSort;
  } = { pendingFirst: true, sort: 'startTimeDesc' };

  protected get filteredSpecialistOptions(): Array<{ id: string; label: string; clinicId: string | null }>
  {
    const clinicId = this.isSystemAdmin
      ? this.selectedGlobalClinicId?.trim() ?? ''
      : this.filtersForm.getRawValue().clinicId?.trim() ?? '';
    if (!clinicId) {
      return this.specialistOptions;
    }

    return this.specialistOptions.filter((item) => !item.clinicId || item.clinicId === clinicId);
  }

  protected get canOpenCreateModal(): boolean {
    return this.canCreateDowntimeRequest && !this.isReferenceOptionsLoading;
  }

  public ngOnInit(): void
  {
    const role = this.authService.getCurrentRole();
    this.isSystemAdmin = role === 'system_admin';
    this.selectedGlobalClinicId = this.systemAdminContext.getClinicId();
    this.canModerateRequests = role === 'manager' || role === 'company_admin' || role === 'system_admin';
    this.canManageDowntime = role === 'manager' || role === 'company_admin' || role === 'system_admin';
    this.canFinishDowntime = role === 'manager' || role === 'company_admin' || role === 'system_admin' || role === 'specialist';
    this.canCreateDowntimeRequest =
      role === 'company_admin' || role === 'manager' || role === 'specialist' || role === 'system_admin';
    this.canOpenSpecialist = role !== 'specialist' && this.canCreateDowntimeRequest;
    this.showSpecialistColumn = role !== 'specialist';
    if (this.isSystemAdmin) {
      this.subscriptions.add(this.systemAdminContext.companyId$.pipe(distinctUntilChanged()).subscribe((companyId) => {
        this.referenceOptionsLoadedForCompanyId = null;
        if (!companyId?.trim()) {
          this.clinicOptions = [];
          this.specialistOptions = [];
          this.isReferenceOptionsLoading = false;
          return;
        }
        this.ensureReferenceOptionsLoaded();
      }));
      this.subscriptions.add(
        combineLatest([
          this.systemAdminContext.companyId$.pipe(distinctUntilChanged()),
          this.systemAdminContext.clinicId$.pipe(distinctUntilChanged()),
        ]).subscribe(([, clinicId]) => {
          this.selectedGlobalClinicId = clinicId;
          this.ensureReferenceOptionsLoaded();
          this.loadDowntime(this.getEffectiveFilters(), false);
        }),
      );
      return;
    }

    this.ensureReferenceOptionsLoaded();
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  protected openCreateModal(): void
  {
    this.createPreviewImpact = null;
    this.pendingCreatePayload = null;
    this.createErrorText = null;
    this.isCreateSubmitting = false;
    this.ensureReferenceOptionsLoaded();
    if (!this.canOpenCreateModal) {
      return;
    }
    this.isCreateModalOpen = true;
  }

  protected closeCreateModal(): void
  {
    if (this.isCreateSubmitting) {
      return;
    }

    this.isCreateModalOpen = false;
    this.createErrorText = null;
    this.createPreviewImpact = null;
    this.pendingCreatePayload = null;
  }

  protected onCreateSubmitted(payload: CreateSpecialistDowntimeRequest): void
  {
    if (this.isCreateSubmitting) {
      return;
    }
    this.isCreateSubmitting = true;
    this.createErrorText = null;
    this.createPreviewImpact = null;
    this.pendingCreatePayload = null;

    this.downtimeService.previewImpact(payload).subscribe({
      next: (preview: SpecialistDowntimeImpactPreview) =>
      {
        this.createPreviewImpact = preview;
        this.pendingCreatePayload = payload;
        this.isCreateSubmitting = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to preview specialist downtime impact', error);
        this.createErrorText = this.resolveCreateErrorText(
          error,
          'Backend rejected specialist downtime impact preview request.',
        );
        this.isCreateSubmitting = false;
      }
    });
  }

  protected backToCreateForm(): void
  {
    if (this.isCreateSubmitting) {
      return;
    }

    this.createPreviewImpact = null;
    this.createErrorText = null;
  }

  protected confirmCreateDowntime(): void
  {
    if (this.isCreateSubmitting || !this.pendingCreatePayload) {
      return;
    }

    this.createDowntime(this.pendingCreatePayload);
  }

  private createDowntime(payload: CreateSpecialistDowntimeRequest): void
  {
    this.isCreateSubmitting = true;
    this.createErrorText = null;

    this.downtimeService.create(payload).subscribe({
      next: (downtime: SpecialistDowntimeItem) =>
      {
        this.isCreateSubmitting = false;
        this.closeCreateModal();
        this.loadDowntime(this.getEffectiveFilters(), false);
        this.openDowntimeQuickView(downtime);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to create specialist downtime', error);
        this.createErrorText = this.resolveCreateErrorText(
          error,
          'Backend rejected specialist downtime creation request.',
        );
        this.isCreateSubmitting = false;
      }
    });
  }

  private resolveCreateErrorText(error: unknown, fallback: string): string
  {
    return resolveHttpErrorMessageKey(error, {
      forbidden: 'error.forbiddenDowntimeCreate',
      badRequestByMessage: {
        'startTime must align to 15-minute slot grid': 'error.downtimeStartSlotGrid',
        'expectedRepairUntil must align to 15-minute slot grid': 'error.downtimeEndSlotGrid',
        'expectedRepairUntil must be greater than startTime': 'error.downtimeEndAfterStart',
        'Invalid downtime time range': 'error.downtimeInvalidTimeRange',
      },
      fallback
    });
  }

  protected openFilterModal(): void
  {
    this.ensureReferenceOptionsLoaded();
    this.isFilterModalOpen = true;
  }

  protected closeFilterModal(): void
  {
    this.isFilterModalOpen = false;
  }

  protected onDelete(item: SpecialistDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.delete(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to delete specialist downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onResolve(item: SpecialistDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.resolve(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to finish specialist downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onApprove(item: SpecialistDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.approve(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to approve specialist downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onReject(item: SpecialistDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.reject(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to reject specialist downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected openSpecialistQuickView(specialistUserId: string): void
  {
    const normalized = specialistUserId.trim();
    if (!this.canOpenSpecialist || !normalized) {
      return;
    }

    this.selectedSpecialistUserId = normalized;
    this.isSpecialistQuickViewOpen = true;
  }

  protected closeSpecialistQuickView(): void
  {
    this.isSpecialistQuickViewOpen = false;
    this.selectedSpecialistUserId = null;
  }

  protected openDowntimeQuickView(item: SpecialistDowntimeItem): void
  {
    this.selectedDowntime = item;
    this.isDowntimeQuickViewOpen = true;
    this.downtimeService.getById(item.id).subscribe({
      next: (detail: SpecialistDowntimeItem) =>
      {
        if (this.isDowntimeQuickViewOpen && this.selectedDowntime?.id === detail.id) {
          this.selectedDowntime = detail;
        }
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load specialist downtime quick view detail', error);
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected closeDowntimeQuickView(): void
  {
    this.isDowntimeQuickViewOpen = false;
    this.selectedDowntime = null;
  }

  protected onApplyFilters(): void
  {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      clinicId: this.isSystemAdmin
        ? this.selectedGlobalClinicId?.trim() || undefined
        : value.clinicId?.trim() || undefined,
      specialistUserId: value.specialistUserId?.trim() || undefined,
      status: (value.status?.trim() as 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected' | '') || 'all',
      pendingFirst: !!value.pendingFirst,
      sort: this.normalizeSort(value.sort),
    };
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  protected onResetFilters(): void
  {
    this.filtersForm.reset({
      clinicId: this.isSystemAdmin ? (this.selectedGlobalClinicId ?? '') : '',
      specialistUserId: '',
      status: 'all',
      pendingFirst: true,
      sort: 'startTimeDesc',
    });

    this.appliedFilters = this.isSystemAdmin
      ? { clinicId: this.selectedGlobalClinicId?.trim() || undefined, pendingFirst: true, sort: 'startTimeDesc' }
      : { pendingFirst: true, sort: 'startTimeDesc' };
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  protected loadMoreDowntime(): void
  {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreItems || !this.nextCursor) {
      return;
    }

    this.loadDowntime(this.getEffectiveFilters(), true);
  }

  protected refresh(): void
  {
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  protected onClinicFilterChanged(): void
  {
    if (this.isSystemAdmin) {
      return;
    }

    const selectedSpecialistId = this.filtersForm.getRawValue().specialistUserId?.trim() ?? '';
    if (!selectedSpecialistId) {
      return;
    }

    const stillAvailable = this.filteredSpecialistOptions.some((item) => item.id === selectedSpecialistId);
    if (!stillAvailable) {
      this.filtersForm.patchValue({ specialistUserId: '' }, { emitEvent: false });
    }
  }

  protected downtimeStatusLabel(status: 'pending_approval' | 'active' | 'resolved' | 'rejected'): string {
    switch (status) {
      case 'pending_approval':
        return 'Pending approval';
      case 'active':
        return 'Active';
      case 'resolved':
        return 'Resolved';
      case 'rejected':
        return 'Rejected';
    }
  }

  private getEffectiveFilters(): {
    clinicId?: string;
    specialistUserId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst: boolean;
    sort: SpecialistDowntimeListSort;
  } {
    if (!this.isSystemAdmin) {
      return this.ensureDefaultSort(this.appliedFilters);
    }

    return {
      ...this.ensureDefaultSort(this.appliedFilters),
      clinicId: this.selectedGlobalClinicId?.trim() || undefined,
    };
  }

  private loadDowntime(
    filters: {
      clinicId?: string;
      specialistUserId?: string;
      status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
      pendingFirst: boolean;
      sort: SpecialistDowntimeListSort;
    },
    append: boolean,
  ): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.nextCursor = null;
      this.hasMoreItems = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      this.actionErrorText = null;
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    this.isLoading = !append;
    this.isLoadingMore = append;
    this.errorText = null;
    this.actionErrorText = null;

    this.downtimeService.getPage({
      limit: this.pageSize,
      cursor: append ? this.nextCursor : null,
      sort: filters.sort,
      pendingFirst: filters.pendingFirst,
      filters: {
        ...filters,
        status: filters.status === 'all' ? undefined : filters.status,
      },
    }).subscribe({
      next: (page) =>
      {
        this.items = append ? [...this.items, ...page.items] : page.items;
        this.nextCursor = page.nextCursor;
        this.hasMoreItems = page.hasMore;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load specialist downtime', error);
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeList',
          fallback: 'Backend returned an error while loading specialist downtime.'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  private normalizeSort(value: string | null | undefined): SpecialistDowntimeListSort {
    switch (value) {
      case 'startTimeAsc':
      case 'expectedRepairUntilDesc':
      case 'expectedRepairUntilAsc':
      case 'createdAtDesc':
      case 'createdAtAsc':
      case 'updatedAtDesc':
      case 'updatedAtAsc':
      case 'statusAsc':
      case 'statusDesc':
        return value;
      case 'startTimeDesc':
      default:
        return 'startTimeDesc';
    }
  }

  private ensureDefaultSort(filters: {
    clinicId?: string;
    specialistUserId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst?: boolean;
    sort?: SpecialistDowntimeListSort;
  }): {
    clinicId?: string;
    specialistUserId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst: boolean;
    sort: SpecialistDowntimeListSort;
  } {
    return {
      ...filters,
      pendingFirst: filters.pendingFirst ?? true,
      sort: filters.sort ?? 'startTimeDesc',
    };
  }

  private loadReferenceOptions(): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.clinicOptions = [];
      this.specialistOptions = [];
      this.isReferenceOptionsLoading = false;
      return;
    }

    this.isReferenceOptionsLoading = true;

    forkJoin({
      context: this.downtimeService.getCreateContext(),
      clinicSettings: this.settingsService.getClinicOperationalSettings(
        this.isSystemAdmin ? this.systemAdminContext.getCompanyId()?.trim() || undefined : undefined,
      ).pipe(
        catchError(() => of([] as ClinicOperationalSettingsDto[])),
      ),
    }).subscribe({
      next: ({ context, clinicSettings }) => {
        this.clinicOptions = this.mergeClinicSettings(context.clinics, clinicSettings);
        this.specialistOptions = context.specialists;
        this.isReferenceOptionsLoading = false;
      },
      error: () => {
        this.clinicOptions = [];
        this.specialistOptions = [];
        this.isReferenceOptionsLoading = false;
      },
    });
  }

  private mergeClinicSettings(
    clinics: Array<{ id: string; label: string }>,
    settings: ClinicOperationalSettingsDto[],
  ): SpecialistDowntimeClinicOption[]
  {
    const settingsByClinicId = new Map(settings.map((item) => [item.clinicId, item]));

    return clinics.map((clinic) =>
    {
      const clinicSettings = settingsByClinicId.get(clinic.id);
      return {
        ...clinic,
        workingDayStart: clinicSettings?.workingDayStart ?? null,
        workingDayEnd: clinicSettings?.workingDayEnd ?? null,
        slotDurationMinutes: clinicSettings?.effectiveSlotDurationMinutes ?? null,
      };
    });
  }

  private ensureReferenceOptionsLoaded(): void
  {
    const companyId = this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : 'current-company';

    if (this.referenceOptionsLoadedForCompanyId === companyId) {
      return;
    }

    this.referenceOptionsLoadedForCompanyId = companyId;
    this.loadReferenceOptions();
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }
}
